/**
 * List view for "several past cases matched" (#661).
 *
 * Purely presentational and GraphQL-free on purpose: the archive search mode
 * feeds it results from the fast `conversationSearch` query, and the agent
 * assistant can feed it the same shape from a tool call — one list view for
 * every place that has to show multiple conversations rather than one answer.
 *
 * A row is a conversation, but it leads with the message that actually matched
 * ("in this case, this reply"), because the agent is re-finding a specific mail
 * rather than reading a case summary.
 */
import { FileText, Search, Sparkles } from "lucide-react";
import { Fragment, type ReactNode } from "react";

/** Highlight markers emitted by the backend's ts_headline. Snippets are plain
 * text — never HTML — so the markers are converted to <mark> here instead of
 * rendering anything the message body could have injected. */
const HIGHLIGHT_START = "[[[";
const HIGHLIGHT_END = "]]]";
const HIGHLIGHT_RE = /\[\[\[(.*?)\]\]\]/g;

export interface ConversationSearchResultItem {
    conversationId: string;
    title: string | null;
    /** Display name for the contact / requester, already resolved by the host. */
    subtitle?: string | null;
    /** ts_headline snippet with [[[ ]]] highlight markers, or plain text. */
    snippet?: string | null;
    /** The specific message that matched, for deep-linking into the thread. */
    matchedMessageId?: string | null;
    /** Preformatted date label; the host owns locale formatting. */
    dateLabel?: string | null;
    /** Which retrieval arms hit: "keyword" | "attachment" | "semantic". */
    matchTypes?: readonly string[];
}

const MATCH_TYPE_META: Record<
    string,
    { label: string; icon: typeof Search; className: string }
> = {
    keyword: {
        label: "Stichwort",
        icon: Search,
        className: "bg-[#EBEFFD] text-[#1F368F]",
    },
    attachment: {
        label: "Anhang",
        icon: FileText,
        className: "bg-[#CCF4FD] text-[#034251]",
    },
    semantic: {
        label: "Sinnverwandt",
        icon: Sparkles,
        className: "bg-[#FBF1FC] text-[#8E4799]",
    },
};

function renderSnippet(text: string): ReactNode[] {
    const parts: ReactNode[] = [];
    const regex = new RegExp(HIGHLIGHT_RE.source, "g");
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(
                <Fragment key={key++}>{text.slice(lastIndex, match.index)}</Fragment>,
            );
        }
        parts.push(
            <mark
                key={key++}
                className="rounded-sm bg-[#F7F99E] px-0.5 text-foreground"
            >
                {match[1]}
            </mark>,
        );
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>);
    }
    return parts;
}

export interface ConversationSearchResultsProps {
    results: readonly ConversationSearchResultItem[];
    /** Opens the case. Receives the matched message so the host can deep-link to it. */
    onOpen: (conversationId: string, matchedMessageId?: string | null) => void;
    /** Highlights the row currently open in a detail pane, if the host has one. */
    selectedConversationId?: string | null;
    emptyLabel?: string;
}

export function ConversationSearchResults({
    results,
    onOpen,
    selectedConversationId,
    emptyLabel = "Keine Treffer",
}: ConversationSearchResultsProps) {
    if (results.length === 0) {
        return (
            <p className="py-12 text-center text-sm text-muted-foreground">
                {emptyLabel}
            </p>
        );
    }

    return (
        <ul className="flex flex-col gap-2">
            {results.map((result) => {
                const isSelected = result.conversationId === selectedConversationId;
                return (
                    <li key={result.conversationId}>
                        <button
                            type="button"
                            className={`w-full rounded-lg border bg-card p-4 text-left transition-colors hover:border-[#345AEF]/40 hover:bg-[#EBEFFD]/40 ${
                                isSelected ? "border-[#345AEF] bg-[#EBEFFD]/60" : ""
                            }`}
                            onClick={() =>
                                onOpen(result.conversationId, result.matchedMessageId)
                            }
                        >
                            <div className="flex items-start justify-between gap-3">
                                <span className="truncate text-sm font-semibold text-[#07195F]">
                                    {result.title ?? "Ohne Betreff"}
                                </span>
                                {result.dateLabel && (
                                    <span className="shrink-0 text-xs text-muted-foreground">
                                        {result.dateLabel}
                                    </span>
                                )}
                            </div>

                            {result.subtitle && (
                                <p className="truncate text-xs text-muted-foreground">
                                    {result.subtitle}
                                </p>
                            )}

                            {result.snippet && (
                                <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                                    {renderSnippet(result.snippet)}
                                </p>
                            )}

                            {result.matchTypes && result.matchTypes.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {result.matchTypes.map((type) => {
                                        const meta = MATCH_TYPE_META[type];
                                        if (!meta) return null;
                                        const Icon = meta.icon;
                                        return (
                                            <span
                                                key={type}
                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.className}`}
                                            >
                                                <Icon className="h-3 w-3" />
                                                {meta.label}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

export { HIGHLIGHT_START, HIGHLIGHT_END, MATCH_TYPE_META, renderSnippet };
